import type {
  CSSProperties,
  ForwardedRef,
  MouseEventHandler,
  ReactNode,
} from 'react';

import { useTabContext } from '#/containers/TabContext';
import { useIsExternalUrl } from '#/hooks/useIsExternalUrl';
import { IDEALIST_UTM_PARAMS } from '#/utils/constants/general/idealistUtmParams';
import { hasUTMParam } from '#/utils/url/hasUTMParam';
import { pathWithParams } from '#/utils/url/pathWithParams';

type UnstyledLinkProps = {
  children?: ReactNode;
  className?: string;
  target?: string;
  href: string;
  rel?: string;
  noIndex?: boolean;
  style?: CSSProperties;
  qaId?: string;
  tabIndex?: number;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  disabled?: boolean;
  title?: string;
  anchorRef?: ForwardedRef<HTMLAnchorElement>;
};

export function UnstyledNativeLink({
  anchorRef,
  children,
  href,
  rel,
  noIndex,
  style,
  target,
  qaId,
  ...props
}: UnstyledLinkProps) {
  const { isExternalUrl } = useIsExternalUrl();
  const linkRel = isExternalUrl(href)
    ? `noopener noreferrer${noIndex ? ' noindex' : ''}`
    : rel;
  const linkTarget = isExternalUrl(href) ? '_blank' : target;
  const isTabbing = useTabContext();

  return (
    <a
      ref={anchorRef}
      href={
        isExternalUrl(href) && !hasUTMParam(href)
          ? pathWithParams(href, IDEALIST_UTM_PARAMS)
          : href
      }
      data-qa-id={qaId}
      style={isTabbing ? style : { ...style, outline: 0 }}
      rel={linkRel}
      target={linkTarget}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children}
    </a>
  );
}
